import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useEvaluationPeriodsApi from './config';
import PeriodWizard from './PeriodWizard';
import PeriodExtendDrawer from './PeriodExtendDrawer';
import { EXTENDABLE_STATUSES } from './periodExtension';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// PRD PM §3.3 durum makinesi (bkz. backend PeriodStateMachine.Manual) — yalnız
// bu geçişler İK tarafından elle tetiklenebilir; diğerleri (evaluation→
// calibration, →pending_close, calibration→pending_close) otomatik işçi
// tarafından yürütülür, satır aksiyonunda YOKTUR.
//
// Faz B (spec 2026-08-07 §6, Task 3 inceleme bulgusu → Task 6) — "calibration"
// durumuna "cancel" eklendi: PeriodStateMachine.Manual artık ("calibration",
// "cancelled") geçişine izin veriyor ("kilitlenme kaçışı" — bir
// CalibrationSession aktifken dönem AllCalibrationsDone hiç true olmaz,
// otomatik "calibration"→"pending_close" hiç tetiklenmez; İK'nın bu satırdan
// elle iptal edebilmesi gerekiyor). CancelAsync'in kendisi zaten
// ICalibrationSessionService.CancelActiveSessionsForPeriodAsync kaskadını
// çağırıyor — burada yalnız FE kapısı açılıyor, ConfirmDialog deseni AYNEN
// (mevcut cancelConfirm metni/type="danger" korunuyor).
const MANUAL_ACTIONS_BY_STATUS = {
  draft: ['activate'],
  active: ['cancel'],
  evaluation: ['cancel'],
  calibration: ['cancel'],
  pending_close: ['close'],
};

export default function EvaluationPeriodsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  // Spec 2026-10-05 §15/4 — satır dönem detayını açar (salt okuma; yazma yetkisi gerekmez).
  const handleRowClick = useCallback((row) => history.push(`/performance/periods/${row.id}`), [history]);
  const { canWrite } = useAuth();

  const [wizardOpen, setWizardOpen] = useState(false);
  // Faz J (spec 2026-08-08-pm-hijyen-faz-j §3/§4, J1, Task 2) — draft dönem
  // düzenleme: satırın "Düzenle" aksiyonu bu satırı (zaten liste yanıtından
  // gelen TAM DTO — ToResponse Create ile simetrik, ayrı bir GetById çağrısı
  // GEREKMEZ) burada tutar; PeriodWizard bu prop'un varlığına göre edit moduna
  // geçer (bkz. PeriodWizard.js `period` prop yorumu).
  const [editingPeriod, setEditingPeriod] = useState(null);
  // Spec 2026-10-09 §9 — Süreyi Uzat çekmecesinin satırı.
  const [extendingPeriod, setExtendingPeriod] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [lifecycleConfirm, setLifecycleConfirm] = useState({
    isOpen: false, verb: null, rowId: null, title: '', message: '',
  });
  const [lifecycleLoading, setLifecycleLoading] = useState(false);

  const {
    fetching, columns: baseColumns, data, pagination,
    create, update, action, extend,
  } = useEvaluationPeriodsApi(queryParams);

  const handleNewButtonClick = useCallback(() => setWizardOpen(true), []);
  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setEditingPeriod(null);
  }, []);

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const openLifecycleConfirm = useCallback((verb, row) => {
    // UI düzeltme demeti #69 (2026-08-09) — "enable-disclosure" durum-makinesi
    // verb'i DEĞİL (Status "closed" kalır) ama AYNI generic action(id, verb)
    // çağrı yolunu (POST /{id}/{verb}) ve AYNI ConfirmDialog mekanizmasını
    // paylaşır — yeni bir state/akış İCAT EDİLMEDİ. Başlık anahtarı
    // "performance.period.${verb}" kalıbından BİLİNÇLİ AYRILDI (kebab-case
    // verb "enable-disclosure" doğrudan interpolasyonla camelCase anahtar
    // kuralını bozardı).
    const titleKey = verb === 'enable-disclosure' ? 'performance.period.enableDisclosure' : `performance.period.${verb}`;
    const messageKey = verb === 'activate'
      ? 'performance.period.activateConfirm'
      : verb === 'close'
        ? 'performance.period.closeConfirm'
        : verb === 'enable-disclosure'
          ? 'performance.period.enableDisclosureConfirm'
          : 'performance.period.cancelConfirm';

    setLifecycleConfirm({
      isOpen: true,
      verb,
      rowId: row.id,
      title: t(titleKey),
      message: t(messageKey),
    });
  }, [t]);

  const handleLifecycleConfirm = useCallback(async () => {
    setLifecycleLoading(true);
    await action(lifecycleConfirm.rowId, lifecycleConfirm.verb);
    setLifecycleConfirm({ isOpen: false, verb: null, rowId: null, title: '', message: '' });
    setLifecycleLoading(false);
  }, [action, lifecycleConfirm]);

  // Faz J (Task 2, J1) — "Düzenle" durum-makinesi verb'i DEĞİL (BE'de bir
  // /{id}/edit ucu yok, PUT {id} doğrudan çağrılır) — bu yüzden
  // MANUAL_ACTIONS_BY_STATUS'a KATILMADI, yalnız draft satırlara AYRICA
  // eklenir (spec: "dönem listesinde draft satırına Düzenle").
  //
  // UI düzeltme demeti #69 (2026-08-09) — "Tebliği Aç" AYNI gerekçeyle
  // MANUAL_ACTIONS_BY_STATUS'a KATILMADI: durum ("closed") DEĞİŞMEZ, yalnız
  // DiscloseFinalLevel bayrağı açılır — bu yüzden satır KOŞULU status DEĞİL,
  // "closed && !discloseFinalLevel" (zaten tebliği açık dönemde AKSİYON
  // GÖSTERİLMEZ — tek yönlü, kapatma ucu YOK).
  const buildActionOptions = useCallback((row) => {
    if (!canWrite('PM')) return [];

    const editActions = row?.status === 'draft'
      ? [{ type: 'edit', label: t('common.edit'), onClick: (r) => setEditingPeriod(r) }]
      : [];

    // Spec 2026-10-09 §9 — "Süreyi Uzat" durum makinesi verb'i DEĞİL (durum değişmez); ayrı çekmece.
    const extendActions = EXTENDABLE_STATUSES.includes(row?.status)
      ? [{ type: 'reschedule', label: t('performance.period.extend.action'), onClick: (r) => setExtendingPeriod(r) }]
      : [];

    const disclosureActions = (row?.status === 'closed' && !row?.discloseFinalLevel)
      ? [{
        type: 'publish',
        label: t('performance.period.enableDisclosure'),
        onClick: (r) => openLifecycleConfirm('enable-disclosure', r),
      }]
      : [];

    const verbs = MANUAL_ACTIONS_BY_STATUS[row?.status] || [];
    const lifecycleActions = verbs.map((verb) => ({
      type: verb === 'activate' ? 'submit_for_approval' : verb === 'close' ? 'close' : 'cancel',
      label: t(`performance.period.${verb}`),
      onClick: (r) => openLifecycleConfirm(verb, r),
    }));

    return [...editActions, ...extendActions, ...lifecycleActions, ...disclosureActions];
  }, [t, canWrite, openLifecycleConfirm]);

  const columns = useMemo(() => {
    if (!canWrite('PM')) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            const actions = buildActionOptions(row);
            return <OptionsMenu actions={actions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, canWrite, data, buildActionOptions]);

  return (
    <Container>
      {(wizardOpen || editingPeriod) && (
        <PeriodWizard
          onClose={closeWizard}
          create={create}
          update={update}
          activate={(id) => action(id, 'activate')}
          period={editingPeriod}
        />
      )}

      <PeriodExtendDrawer
        period={extendingPeriod}
        open={Boolean(extendingPeriod)}
        onClose={() => setExtendingPeriod(null)}
        onSave={async (payload) => {
          const ok = await extend(extendingPeriod.id, payload);
          if (ok) setExtendingPeriod(null);
          return ok;
        }}
      />

      <ConfirmDialog
        isOpen={lifecycleConfirm.isOpen}
        title={lifecycleConfirm.title}
        message={lifecycleConfirm.message}
        onConfirm={handleLifecycleConfirm}
        onCancel={() => setLifecycleConfirm({ isOpen: false, verb: null, rowId: null, title: '', message: '' })}
        loading={lifecycleLoading}
        // "enable-disclosure" GERİ ALINAMAZ (tek yönlü, kapatma ucu YOK) —
        // "cancel" ile AYNI "danger" görseliyle uyarılır.
        type={['cancel', 'enable-disclosure'].includes(lifecycleConfirm.verb) ? 'danger' : 'info'}
      />

      {useMemo(() => (
        <DataTable
          title={t('performance.period.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('PM')}
          newButtonLabel={t('performance.period.new')}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
